import { FormLeavingListener } from '@exam-center/components';
import {
  AdminGetExamCenterProctorByIdDocument,
  GetExamCenterProctorByIdDocument,
  AdminSearchExamCenterProctorsDocument,
  SearchExamCenterProctorsDocument,
  useUpdateExamCenterProctorFromProctorAdminMutation,
} from '@exam-center/graphql';
import { ConfirmationModal, Text } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { useEffect, useState } from 'react';

import { AdminProctorDetailsForm } from '../../components';
import { useGetAgreementStorageObjectsFile } from '../../hooks';
import { useAdminProctorDetailsForm, useAdminProctorDetails } from '../../providers';

function getConfirmationContent(hasAssignmentChanges: boolean) {
  switch (hasAssignmentChanges) {
    case true:
      return (
        <Text>
          การเปลี่ยนแปลงครั้งนี้จะส่งผลต่อ{' '}
          <Text variant='body1Semibold' component='span'>
            สถานะการระงับสิทธิ์สอบ
          </Text>
          <br />
          กรุณาตรวจสอบให้แน่ใจก่อนดำเนินการ
        </Text>
      );
    case false:
      return <Text>คุณกำลังจะแก้ไขข้อมูลผู้คุมสอบ กรุณาตรวจสอบให้แน่ใจก่อนดำเนินการ</Text>;
  }
}

export default function AdminProctorDetailsFormLogic() {
  const { proctor } = useAdminProctorDetails();

  const formContext = useAdminProctorDetailsForm();
  const { setValue, watch } = formContext;
  const { showSnackbar } = useSnackbar();
  const [isTerminationDirty, setIsTerminationDirty] = useState(false);

  const [updateProctor, { loading }] = useUpdateExamCenterProctorFromProctorAdminMutation({
    onCompleted: () => {
      showSnackbar({ message: 'บันทึกข้อมูลเรียบร้อยแล้ว', variant: 'success' });
    },
    onError: () => {
      showSnackbar({ message: 'เกิดข้อผิดพลาดในการบันทึกข้อมูล', variant: 'error' });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      AdminSearchExamCenterProctorsDocument,
      SearchExamCenterProctorsDocument,
      AdminGetExamCenterProctorByIdDocument,
      GetExamCenterProctorByIdDocument,
    ],
  });

  const [isConfirmModalOpen, confirmModalOpenHandler] = useDisclosure();

  const handleSubmit = () => {
    confirmModalOpenHandler.open();
  };

  const handleConfirmUpdateExamCenterProctor = async () => {
    const data = formContext.getValues();
    await updateProctor({
      variables: {
        updateExamCenterProctorFromProctorAdminInput: {
          examCenterProctorId: proctor.id,
          info: {
            titleEn: data.titleEn?.value ? data.titleEn.value : null,
            titleTh: data.titleTh?.value,
            phoneNumber: data.phoneNumber,
            metadata: {
              position: data.employeeInfo.position,
              employeeId: data.employeeInfo.code,
              department: data.employeeInfo.department,
            },
            lastNameTh: data.lastNameTh,
            lastNameEn: data.lastNameEn ? data.lastNameEn : null,
            firstNameTh: data.firstNameTh,
            firstNameEn: data.firstNameEn ? data.firstNameEn : null,
            endedAt: data.employeeInfo.termination.endedAt,
            email: data.email,
            address: data.address,
          },
        },
      },
    });
    confirmModalOpenHandler.close();
  };
  const hasEndedAt = !!proctor?.endedAt;

  const agreementStorageObjectsFile = useGetAgreementStorageObjectsFile({
    proctor,
  });

  const shouldDisplayEndDate = watch('employeeInfo.termination.isAssigned') === 'true';

  useEffect(() => {
    if (!shouldDisplayEndDate) {
      setValue('employeeInfo.termination.endedAt', null);
    }
  }, [shouldDisplayEndDate, setValue]);

  useEffect(() => {
    if (formContext.formState.dirtyFields?.employeeInfo?.termination) {
      setIsTerminationDirty(true);
    }
  }, [formContext.formState.dirtyFields?.employeeInfo?.termination]);

  return (
    <>
      <AdminProctorDetailsForm
        onSubmit={formContext.handleSubmit(handleSubmit)}
        isSubmitting={loading}
        fields={{
          personal: {
            titleTh: { name: 'titleTh' },
            firstNameTh: { name: 'firstNameTh' },
            lastNameTh: { name: 'lastNameTh' },
            titleEn: { name: 'titleEn' },
            firstNameEn: { name: 'firstNameEn' },
            lastNameEn: { name: 'lastNameEn' },
            email: { name: 'email' },
            phoneNumber: { name: 'phoneNumber' },
            address: { name: 'address' },
          },
          employeeInfo: {
            code: { name: 'employeeInfo.code' },
            position: { name: 'employeeInfo.position' },
            department: { name: 'employeeInfo.department' },
            termination: {
              isAssigned: { name: 'employeeInfo.termination.isAssigned' },
              endedAt: { name: 'employeeInfo.termination.endedAt' },
            },
          },
        }}
        values={{
          idNumber: proctor?.idNumber ?? '-',
          proctorType: proctor?.type,
          email: proctor?.email ?? '-',
          agreementStorageObjectsFile: agreementStorageObjectsFile,
          startedAt: proctor?.startedAt ?? '-',
          termination: {
            isAssigned: hasEndedAt,
            endedAt: proctor?.endedAt ?? undefined,
          },
        }}
        options={{
          shouldShowEndedAtDateDisplay: shouldDisplayEndDate,
        }}
      />
      <FormLeavingListener onSubmit={confirmModalOpenHandler.open} />
      <ConfirmationModal
        title='ยืนยันการแก้ไขข้อมูล?​'
        open={isConfirmModalOpen}
        onClose={confirmModalOpenHandler.close}
        onAccept={handleConfirmUpdateExamCenterProctor}
        onDiscard={confirmModalOpenHandler.close}
        discardText='ยกเลิก'
        acceptText='ยืนยัน'
        content={getConfirmationContent(isTerminationDirty)}
      />
    </>
  );
}
